import React, {useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControl from '@mui/material/FormControl';
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from '@mui/material/InputLabel';
import ArgoListPagingControlsHook from "./ArgoListPagingControlsHook";
import ArgoTextFieldHook from "./ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "./ArgoTextFieldSelectHook";
import ArgoMultipleSelectCheckmarksHook from "./ArgoMultipleSelectCheckmarksHook";
import ArgoButtonHook from "./ArgoButtonHook";
import { setFilter } from "../../actions/setFilter";
import { updatePageAndGetList } from "../../actions/templateListActions";
import { getTagDDLB } from "../../actions/tagActions";
import { pageableObjFromFilter } from "../../utilities/PageableAndFilterUtilities";
import { toLocale } from '../../utilities/dateFormats';
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import ArgoListTitleAndLoadingAnimationHook from "./ArgoListTitleAndLoadingAnimationHook";
import Box from "@mui/material/Box";
import * as expander from "../../utilities/expander";
import Typography from "@mui/material/Typography";
import {getLabel} from "../../constants/questionTypeConstants"
import {getGroupLabel} from  "../../constants/groupTypeConstants"

const ArgoSelectExistingHook = (props) => {
    // State hooks to replace class component state
    const [name, setName] = useState("");
    const [nameQualifier, setNameQualifier] = useState("");
    const [updatedBy, setUpdatedBy] = useState("");
    const [selectedTags, setSelectedTags] = useState([]);
    const [sortBy, setSortBy] = useState("lastModifiedDateDesc");
    const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
    const [loading, setLoading] = useState(true);
    const [filterState, setFilterState] = useState(false);

    const isResetting = useRef(false)

    // Redux hooks
    const dispatch = useDispatch();
    
    // Derive filterType based on props.mode
    const filterType = (() => {
        switch(props.mode) {
            case TEMPLATE.TYPE.CATEGORY:
                return TEMPLATE.TYPE.SELECT_EXISTING_CATEGORY;
            case TEMPLATE.TYPE.GROUP:
                return TEMPLATE.TYPE.SELECT_EXISTING_GROUP;
            case TEMPLATE.TYPE.QUESTION:
                return TEMPLATE.TYPE.SELECT_EXISTING_QUESTION;
            default:
                return "";
        }
    })();

    // Get filter and filterTags from Redux
    const filter = useSelector(state => state.filter[TEMPLATE.FILTER.current[filterType]] || {});
    const filterTags = useSelector(state => state.filter[TEMPLATE.FILTER.all.selectExistingTags] || {});

    // Equivalent to componentDidMount
    useEffect(() => {
        // Only questions have a tags filter
        if (props.mode === TEMPLATE.TYPE.QUESTION) {
            dispatch(getTagDDLB(pageableObjFromFilter(filterTags), filterTags));
        }

        // let tempSortBy = "lastModifiedDate";
        // if (filter.title === "Name") {
        //     tempSortBy = "name";
        // }

        setName(filter.title || "");
        setNameQualifier(filter.nameQualifier || "");
        setUpdatedBy(filter.updatedBy || "");
        setSelectedTags(filter.tags || []);
        //setSortBy(tempSortBy + (filter.direction?.charAt(0).toUpperCase() + filter.direction?.slice(1) || "Asc"));

        setSelectedTemplateKey("");
        setLoading(true);
        
        // Call updateFilter after state is set
        updateFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array for componentDidMount

    // Equivalent to componentWillReceiveProps
    useEffect(() => {
        if (filterState) {
            setFilterState(false);
        }
        else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, filterState, loading]); // Dependencies for componentWillReceiveProps

    // Type filter useEffect
    useEffect(() => {
        if (props.useTypeFilter) {
            if ((props.likert && filter.questionType=== "!LIKERT_RADIO") || (!props.likert && filter.questionType === "LIKERT_RADIO")) {
                updateFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.useTypeFilter, props.likert, filter.questionType]);

    useEffect(() => {
        if (isResetting.current) {
            updateFilter();
            isResetting.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isResetting.current]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = useCallback(() => {
        setLoading(false);
    }, []);

    const updateFilter = useCallback(() => {
        const updatedFilter = {...filter};

        updatedFilter.pageNumber = 1;
        
        const sortObj = getSortAndDirection(sortBy);
        updatedFilter.name = sortObj.sort;
        updatedFilter.direction = sortObj.direction;

        updatedFilter.mode = props.mode;
        updatedFilter.title = name;
        updatedFilter.nameQualifier = nameQualifier;
        updatedFilter.updatedBy = updatedBy;
        updatedFilter.tags = selectedTags;

        if (props.useTypeFilter) {
            updatedFilter.questionType = props.likert ? "LIKERT_RADIO" : "!LIKERT_RADIO";
        }
        else {
            updatedFilter.questionType = "";
        }

        dispatch(setFilter(pageableObjFromFilter(updatedFilter), updatedFilter, filterType, TEMPLATE.MODE.CURRENT));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, nameQualifier, updatedBy, selectedTags, sortBy, props.mode, props.likert, props.useTypeFilter, filter, filterType, dispatch]);

    const addTemplate = useCallback(() => {

        props.onAddTemplate({templateKey: selectedTemplateKey}, props.parentTemplate);
        setSelectedTemplateKey("");

        // When a template is added we now need to get template in case it contains showGroupKeys
        // switch(props.mode) {
        //     case TEMPLATE.TYPE.QUESTIONNAIRE:
        //         // I don't think we need anything here
        //         break;
        //     case TEMPLATE.TYPE.CATEGORY:
        //         dispatch(getCategoryTemplate(selectedTemplateKey));
        //         if (props.createMode) {
        //             dispatch(addQuestionnaireTemplateCategoryKey(props.parentTemplate, selectedTemplateKey));
        //         } else {
        //             dispatch(addQuestionnaireCategory(props.parentTemplate, selectedTemplateKey));
        //         }
        //         break;
        //     case TEMPLATE.TYPE.GROUP:
        //         dispatch(getGroupTemplate(selectedTemplateKey));
        //         if (props.createMode) {
        //             dispatch(addCategoryTemplateGroupKey(props.parentTemplate, selectedTemplateKey));
        //         } else {
        //             dispatch(addCategoryGroup(props.parentTemplate, selectedTemplateKey));
        //         }
        //         break;
        //     case TEMPLATE.TYPE.QUESTION:
        //         dispatch(getQuestionTemplate(selectedTemplateKey));
        //         if (props.createMode) {
        //             dispatch(addGroupTemplateQuestionKey(props.parentTemplate, selectedTemplateKey));
        //         } else {
        //             dispatch(addGroupQuestion(props.parentTemplate, selectedTemplateKey));
        //         }
        //         break;
        // }

        // dispatch(expanderClear(props.editorName));
        // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [selectedTemplateKey, props.onAddTemplate, props.parentTemplate]);

    const onTextChange = useCallback((event, fieldName) => {
        const value = event.target.value;

        switch(fieldName) {
            case "name":
                setName(value);
                break;
            case "nameQualifier":
                setNameQualifier(value);
                break;
            case "updatedBy":
                setUpdatedBy(value);
                break;
            default:
                break;
        }
    }, []);

    const handleSelectTagsOnChange = useCallback((event, fieldName, tagList) => {
        setSelectedTags(tagList.filter(tag => tag.checked))
    }, []);

    const onSelectChange = useCallback((event, fieldName) => {
        const value = event.target.value;

        if (fieldName === "sortBy") {
            setSortBy(value);
        }
    }, []);

    const handleSelectTemplateOnChange = useCallback((event) => {
        setSelectedTemplateKey(event.target.value);
    }, []);

    const onPageChange = useCallback((page, size, event) => {
        setLoading(true);
        setFilterState(true);
        dispatch(updatePageAndGetList(page, size, filter, filterType, TEMPLATE.MODE.CURRENT, event));
    }, [filter, filterType, dispatch]);

    const filterAction = useCallback(() => {
        setLoading(true);
        setFilterState(true);
        updateFilter();
    }, [updateFilter]);

    const resetFilter = useCallback(() => {
        isResetting.current = true
        setName("");
        setNameQualifier("");
        setUpdatedBy("");
        setSelectedTags([]);
        setSortBy("lastModifiedDateDesc");
        setSelectedTemplateKey("");
        setLoading(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateFilter]);

    useEffect(() => {
        resetFilter()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.likert]);

    const getSortAndDirection = useCallback((sortByValue) => {
        let sort = "";
        let direction = "";

        switch(sortByValue) {
            case "nameAsc":
                sort = "Name";
                direction = "asc";
                break;
            case "nameDesc":
                sort = "Name";
                direction = "desc";
                break;
            case "lastModifiedDateAsc":
                sort = "Last updated";
                direction = "asc";
                break;
            case "lastModifiedDateDesc":
                sort = "Last updated";
                direction = "desc";
                break;
            // no default
        }

        return {sort: sort, direction: direction};
    }, []);

    const convertToDisplayText = useCallback((questionType) => {
        let displayName = '';
        switch (questionType) {
            case 'CHECKBOX': displayName = 'Checkbox';
                break;
            case 'YES_NO': displayName = 'Yes/No';
                break;
            case 'RADIO': displayName = 'Single Select';
                break;
            case 'LIKERT_RADIO': displayName = 'Likert';
                break;
            case 'NUMBER': displayName = 'Number';
                break;
            case 'PHONE': displayName = 'Phone';
                break;
            case 'DATE': displayName = 'Date';
                break;
            case 'DATE_RANGE': displayName = 'Date Range';
                break;
            case 'EMAIL': displayName = 'Email';
                break;
            case 'FREE_TEXT': displayName = 'Free Text';
                break;
            case 'LIKERT': displayName = 'Likert';
                break;
            case 'ADDRESS_FULL': displayName = 'Address';
                break;
            case 'CURRENCY': displayName = 'Currency';
                break;
            case 'ZIPPOSTAL_ONLY': displayName = 'ZIP Code';
                break;
            case 'STATE_ONLY': displayName = 'State';
                break;
            // no default
        }
        return displayName;
    }, []);

    const formatMenuItem = useCallback((row) => {
        let item;

        let name = row.name;
        item =
            <MenuItem key={row.templateKey} value={row.templateKey}>
                <Box style={{display: "flex", flexDirection: "row", alignItems: "center", minWidth: 235, width: "100%"}} sx={{gap: 1}}>
                    <Box style={{display: "flex", alignItems: "center", minWidth: 45, flex: 1}}>
                        <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 45, color: expander.TEXT_COLOR_SECONDARY}}>Name:</Typography>
                        <Typography
                            style={{
                                flex: 1,
                                minWidth: 0,
                                maxWidth: 940,
                                paddingLeft: 5,
                                color: expander.TEXT_COLOR_PRIMARY,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}>
                            {name}
                        </Typography>
                    </Box>

                    {row.nameQualifier !== null ?
                        <Box style={{display: "flex", alignItems: "center", minWidth: 233}}>
                            <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 60, color: expander.TEXT_COLOR_SECONDARY}}>Qualifier:</Typography>
                            <Typography
                                style={{
                                    flex: 1,
                                    minWidth: 170,
                                    maxWidth: 170,
                                    paddingLeft: 5,
                                    color: expander.TEXT_COLOR_PRIMARY,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>
                                {row.nameQualifier}
                            </Typography>
                        </Box> : ""
                    }

                    {props.mode !== TEMPLATE.TYPE.CATEGORY ?
                        <Box style={{display: "flex", alignItems: "center", minWidth: 114}}>
                            <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 30, color: expander.TEXT_COLOR_SECONDARY}}>Type:</Typography>
                            <Typography
                                style={{
                                    flex: 1,
                                    minWidth: 84,
                                    paddingLeft: 5,
                                    color: expander.TEXT_COLOR_PRIMARY,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>
                                {props.mode === TEMPLATE.TYPE.QUESTION ? getLabel[row.questionType] : getGroupLabel[row.questionGroupType]}
                            </Typography>
                        </Box> : ""
                    }

                    <Box style={{display: "flex", alignItems: "center", minWidth: 100}}>
                        <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 100, color: expander.TEXT_COLOR_SECONDARY}}>Last Modified:</Typography>
                        <Typography
                            style={{
                                flex: 1,
                                minWidth: 0,
                                paddingLeft: 5,
                                color: expander.TEXT_COLOR_PRIMARY,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}>
                            {toLocale(row.lastModifiedDate)}
                        </Typography>
                    </Box>
                </Box>
            </MenuItem>

        return item;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.mode, convertToDisplayText]);

    // const renderSelectedTags = useCallback((values) => {
    //     switch (values.length) {
    //         case 0:
    //             return "";
    //         case 1:
    //             let tagName = "";
    //             for (let x = 0; x < filterTags.list?.length || 0; x++) {
    //                 if (filterTags.list[x].id === values[0]) {
    //                     tagName = filterTags.list[x].name;
    //                     break;
    //                 }
    //             }
    //             return tagName;
    //         default:
    //             return values.length + " tags selected";
    //     }
    // }, [filterTags.list]);

    const renderSelectedTemplate = useCallback((values) => {
        let selectedTemplateDisplay = "";

        for (let x = 0; x < filter.list?.length || 0; x++) {
            if (filter.list[x].templateKey === values) {
                let row = filter.list[x];
                selectedTemplateDisplay = (
                    <MenuItem key={row.templateKey} value={row.templateKey} style={{padding: 6}}>
                        <Box style={{display: "flex", flexDirection: "row", alignItems: "center", minWidth: 235, width: "100%"}} sx={{gap: 1}}>
                            <Box style={{display: "flex", alignItems: "center", minWidth: 45, flex: 1}}>
                                <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 45, color: expander.TEXT_COLOR_SECONDARY}}>Name:</Typography>
                                <Typography
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        maxWidth: 940,
                                        paddingLeft: 5,
                                        color: expander.TEXT_COLOR_PRIMARY,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}>
                                    {row.name}
                                </Typography>
                            </Box>

                            {row.nameQualifier !== null ?
                                <Box style={{display: "flex", alignItems: "center", minWidth: 60}}>
                                    <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 60, color: expander.TEXT_COLOR_SECONDARY}}>Qualifier:</Typography>
                                    <Typography
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                            paddingLeft: 5,
                                            color: expander.TEXT_COLOR_PRIMARY,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}>
                                        {row.nameQualifier}
                                    </Typography>
                                </Box> : ""
                            }

                            {props.mode !== TEMPLATE.TYPE.CATEGORY ?
                                <Box style={{display: "flex", alignItems: "center", minWidth: 30}}>
                                    <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 30, color: expander.TEXT_COLOR_SECONDARY}}>Type:</Typography>
                                    <Typography
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                            paddingLeft: 5,
                                            color: expander.TEXT_COLOR_PRIMARY,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}>
                                        {props.mode === TEMPLATE.TYPE.QUESTION ? getLabel[row.questionType] : getGroupLabel[row.questionGroupType]}
                                    </Typography>
                                </Box> : ""
                            }

                            <Box style={{display: "flex", alignItems: "center", minWidth: 100}}>
                                <Typography style={{flexShrink: 0, paddingLeft: 5, minWidth: 100, color: expander.TEXT_COLOR_SECONDARY}}>Last Modified:</Typography>
                                <Typography
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        paddingLeft: 5,
                                        color: expander.TEXT_COLOR_PRIMARY,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}>
                                    {toLocale(row.lastModifiedDate)}
                                </Typography>
                            </Box>
                        </Box>
                    </MenuItem>);

                break;
            }
        }

        return selectedTemplateDisplay;
    }, [filter.list, props.mode]);

    // Component rendering

    let componentTitle;
    let selectHoverHelp;
    let floatingTextLabel;

    componentTitle = `Select an existing ${props.likert ? "likert" : ""} ${props.mode}`;
    componentTitle = componentTitle + (props.hideCreateButton ? "" : " or");
    selectHoverHelp = `Format: Title, Qualifier, ${props.mode !== "category" ? "Type," : ""} Last Modified Date`;
    floatingTextLabel = `Selected ${props.mode}`;

    return (
        <div id="ArgoSelectExisting-page-container-div" style={{width: "100%"}}>

            <Card id="ArgoSelectExisting-container-card" style={{width: "100%", border: "1px solid", borderColor: "black", boxShadow: "3px 5px lightgray", paddingRight: props.paddingRight}}>
                <CardContent id="ArgoSelectExisting-container-card-content" style={{width: "auto"}}>

                    <div id="ArgoSelectExisting-container-card-content-container-div" style={{width: "100%"}}>

                        <ArgoListTitleAndLoadingAnimationHook
                            fileName="ArgoSelectExistingHook"
                            listTitle={componentTitle}
                            loading={loading}
                            filter={filterState}
                            showOutstandingAlert={false}
                            mode={props.mode}
                            isLikert={props.likert}
                            width={props.width}
                            openEditorCallBack={props.openEditorCallBack}
                            hideCreateButton={props.hideCreateButton}
                        />

                        <Box style={{display: "flex"}} sx={{gap: 3}}>

                            <ArgoTextFieldHook
                                fileName="ArgoSelectExistingHook"
                                fieldName="name"
                                label={"Name"}
                                width="200px"
                                value={name}
                                onChange={onTextChange}
                            />

                            <ArgoTextFieldHook
                                fileName="ArgoSelectExistingHook"
                                fieldName="nameQualifier"
                                label={"Qualifier"}
                                width="140px"
                                value={nameQualifier}
                                onChange={onTextChange}
                            />

                            <ArgoTextFieldHook
                                fileName="ArgoSelectExistingHook"
                                fieldName="updatedBy"
                                label={"Updated by"}
                                width="140px"
                                value={updatedBy}
                                onChange={onTextChange}
                            />

                            {/*--------------------------------------------------------------------------------*/}
                            {/*-- Only Questions use the Tags DDLB*/}
                            {/*--------------------------------------------------------------------------------*/}

                            {props.mode !== "category" && props.mode !== "group" ?
                                <ArgoMultipleSelectCheckmarksHook
                                    fileName="ArgoSelectExistingHook"
                                    fieldName="selectedTags"
                                    label={"Tags"}
                                    width={160}
                                    minWidth={160}
                                    dropDownWidth={200}
                                    dropDownHeight={280}
                                    valueProperty="id"
                                    textProperty="name"
                                    menuItems={filterTags.list}
                                    onChange={handleSelectTagsOnChange}
                                    selectedTags={selectedTags}
                                /> : ""
                            }

                            <ArgoTextFieldSelectHook
                                fileName="ArgoSelectExistingHook"
                                fieldName="sortBy"
                                label="Sort by"
                                value={sortBy}
                                onChange={onSelectChange}
                                fullWidth={false}
                                width="220px"
                                menuItems={[
                                    {value: "nameAsc", text: "Name (ascending)"},
                                    {value: "nameDesc", text: "Name (descending)"},
                                    {value: "lastModifiedDateAsc", text: "Last updated (ascending)"},
                                    {value: "lastModifiedDateDesc", text: "Last updated (descending)"}
                                ]}
                            />

                            <Box id="ArgoSelectExisting-ButtonGroup" variant="outlined"
                                         style={{display: "flex", marginLeft: "auto"}}
                                         sx={{alignItems: "center", gap: 1}}>

                                <ArgoButtonHook
                                    fileName="ArgoSelectExistingHook"
                                    fieldName="filter"
                                    variant="text"
                                    label="Filter"
                                    paddingTop="5px"
                                    width={100}
                                    onClick={filterAction}
                                />

                                <ArgoButtonHook
                                    fileName="ArgoSelectExistingHook"
                                    fieldName="reset"
                                    variant="text"
                                    label="Reset"
                                    width={100}
                                    paddingTop="5px"
                                    onClick={resetFilter}
                                />

                                <ArgoButtonHook
                                    fileName="ArgoSelectExistingHook"
                                    fieldName="add-selected"
                                    label="Add Selected"
                                    width={140}
                                    onClick={addTemplate}
                                    paddingTop="5px"
                                    disabled={selectedTemplateKey === ""}
                                />
                            </Box>
                        </Box>

                        {/*--------------------------------------------------------------------------------*/}
                        {/*-- DDLB Select template.. Categories, Groups or Questions
                                {/*--------------------------------------------------------------------------------*/}

                        <div id="ArgoSelectExisting-container-template-select"  title={selectHoverHelp} style={{width: "100%", paddingBottom: 20}}>
                            <FormControl fullWidth disabled={loading}>
                                <InputLabel id="ArgoSelectExisting-template-input-label" sx={{paddingLeft: "-15px"}}>{floatingTextLabel}</InputLabel>
                                <Select
                                    labelId="ArgoSelectExisting-template-labelId"
                                    id="ArgoSelectExisting-template"
                                    variant="standard"
                                    value={selectedTemplateKey !== undefined ? selectedTemplateKey : ""}
                                    onChange={handleSelectTemplateOnChange}
                                    renderValue={renderSelectedTemplate}
                                >
                                    {filter.list && filter.list.map((row, index) =>
                                        formatMenuItem(row, index)
                                    )}
                                </Select>
                            </FormControl>
                        </div>

                        {/*--------------------------------------------------------------------------------*/}
                        {/*-- Paging controls, Page Size Hidden, always 10 item rows
                                {/*--------------------------------------------------------------------------------*/}

                        <ArgoListPagingControlsHook
                            hidePageSize={true}
                            pageState={filter}
                            onPageChange={onPageChange}
                        />
                    </div>

                </CardContent>
            </Card>
        </div>
    );
};

ArgoSelectExistingHook.defaultProps = {
    mode: TEMPLATE.TYPE.QUESTION,
    likert: false,
    useTypeFilter: false,
    paddingRight: "30px",
    openEditorCallBack: undefined,
    hideCreateButton: false,
    createMode: false
};

ArgoSelectExistingHook.propTypes = {
    mode: PropTypes.string,
    likert: PropTypes.bool,
    useTypeFilter: PropTypes.bool,
    onAddTemplate: PropTypes.func.isRequired,
    paddingRight: PropTypes.string,
    registerResetFilter: PropTypes.func,
    openEditorCallBack: PropTypes.func,
    hideCreateButton: PropTypes.bool,
    createMode: PropTypes.bool,
    parentTemplate: PropTypes.object,
};

export default ArgoSelectExistingHook;